import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, post } from '../../nucleo/api';
import { data } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Feriado } from './tipos';

/** Calendário único de feriados das automações (no lugar do `FERIADOS.txt` do servidor Windows). */
export function Feriados() {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [novo, setNovo] = useState(false);
  const [excluir, setExcluir] = useState<Feriado | null>(null);
  // Val5 (29/10): feriados são configuração (`automacoes.configurar`).
  const podeEditar = s.pode('automacoes.configurar');
  const remover = useMutation({
    mutationFn: (f: Feriado) => del(`/automacoes/feriados/${f.id}`),
    onSuccess: async () => {
      setExcluir(null);
      toast.sucesso(tn('comum.removido'));
      await qc.invalidateQueries({ queryKey: ['automacoes'] });
    },
    onError: (e) => {
      setExcluir(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  const colunas: Coluna<Feriado>[] = [
    { chave: 'data', rotulo: t('colunas.data'), principal: true, mono: true, render: (x) => data(x.data) },
    { chave: 'nome', rotulo: t('colunas.nome') },
  ];
  const campos: DefCampo[] = [
    { nome: 'data', rotulo: t('colunas.data'), tipo: 'data', obrigatorio: true },
    { nome: 'nome', rotulo: t('colunas.nome'), tipo: 'texto', obrigatorio: true },
  ];
  return (
    <>
      {podeEditar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('feriados.novo')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Feriado>
        tela="automacoes.feriados"
        caminho="/automacoes/feriados"
        chaveConsulta={['automacoes', 'feriados']}
        colunas={colunas}
        aoClicar={podeEditar ? setExcluir : undefined}
        vazio={<Vazio texto={t('vazio.feriados')} />}
      />
      {novo && (
        <Drawer
          aberto
          aoFechar={() => setNovo(false)}
          titulo={t('feriados.novo')}
          rodape={
            <>
              <Botao variante="fantasma" onClick={() => setNovo(false)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-feriado">
                {tn('comum.salvar')}
              </Botao>
            </>
          }
        >
          <Formulario
            id="form-feriado"
            campos={campos}
            aoEnviar={async (v) => {
              await post('/automacoes/feriados', { id: crypto.randomUUID(), data: v.data, nome: v.nome });
              await qc.invalidateQueries({ queryKey: ['automacoes'] });
              toast.sucesso(tn('comum.salvo'));
              setNovo(false);
            }}
          />
        </Drawer>
      )}
      <Confirmar
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        aoConfirmar={() => excluir && remover.mutate(excluir)}
        titulo={t('feriados.excluirTitulo', { nome: excluir?.nome ?? '' })}
        texto={t('feriados.excluirTexto')}
        perigoso
        carregando={remover.isPending}
      />
    </>
  );
}
